import { useRef, useMemo, useEffect } from 'react';
import { useFrame, useThree, useLoader } from '@react-three/fiber';
import * as THREE from 'three';

/**
 * Interactive 3D Cube Grid with Image Projection.
 * Displays the original background image projected across the grid of cubes.
 */
export const CubeGrid = () => {
    const meshRef = useRef<THREE.InstancedMesh>(null);
    const materialRef = useRef<THREE.MeshStandardMaterial>(null);
    const { viewport, size } = useThree();
    const mouseRef = useRef({ x: 0, y: 0 });
    const smoothMouseRef = useRef({ x: 0, y: 0 });

    // Load the background image
    // Note: We use useLoader instead of useTexture from drei to keep dependencies minimal if desired, 
    // but since we added three/fiber, useLoader is standard.
    const texture = useLoader(THREE.TextureLoader, '/hero_background.png');

    // Grid configuration matches the original dimensions
    const countRow = 80;
    const countCol = 40;
    const count = countRow * countCol;

    // Calculate grid dimensions for projection
    const spacing = 1.6;
    const gridWidth = countRow * spacing;
    const gridHeight = countCol * spacing;

    const dummy = useMemo(() => new THREE.Object3D(), []);

    // Instance positions for physics calculations
    const particles = useMemo(() => {
        const temp = [];
        for (let i = 0; i < countRow; i++) {
            for (let j = 0; j < countCol; j++) {
                const x = (i - countRow / 2) * spacing;
                const y = (j - countCol / 2) * spacing;
                const z = 0;
                temp.push({ x, y, z, originalX: x, originalY: y, originalZ: z });
            }
        }
        return temp;
    }, [countRow, countCol]);

    // Generate static position data for UV mapping (Mosaic Effect)
    const posArray = useMemo(() => {
        const arr = new Float32Array(count * 3);
        let ptr = 0;
        for (let i = 0; i < countRow; i++) {
            for (let j = 0; j < countCol; j++) {
                const x = (i - countRow / 2) * spacing;
                const y = (j - countCol / 2) * spacing;
                const z = 0;
                arr[ptr++] = x;
                arr[ptr++] = y;
                arr[ptr++] = z;
            }
        }
        return arr;
    }, [countRow, countCol]);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!size.width || !size.height) return;
            const normalizedX = (e.clientX / size.width) * 2 - 1;
            const normalizedY = -(e.clientY / size.height) * 2 + 1;
            mouseRef.current.x = normalizedX;
            mouseRef.current.y = normalizedY;
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, [size.width, size.height]);

    useFrame((state) => {
        if (!meshRef.current) return;

        const time = state.clock.getElapsedTime();

        const smoothMouse = smoothMouseRef.current;
        const targetMouse = mouseRef.current;
        const smoothing = 0.08;
        smoothMouse.x += (targetMouse.x - smoothMouse.x) * smoothing;
        smoothMouse.y += (targetMouse.y - smoothMouse.y) * smoothing;

        const mouseX = (smoothMouse.x * viewport.width) / 2;
        const mouseY = (smoothMouse.y * viewport.height) / 2;

        particles.forEach((particle, i) => {
            const { originalX, originalY } = particle;

            // Distance from center for "Attraction"
            // Lean towards center (0,0)
            const leanStrength = 0.5;

            // Mask Logic (No mask)
            const maskStrength = 1;

            // Interaction Physics
            const dx = mouseX - originalX;
            const dy = mouseY - originalY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const safeDist = dist || 0.0001;

            const influenceRadius = 12;
            const proximity = Math.max(0, 1 - dist / influenceRadius);
            const bulge = Math.pow(proximity, 2) * 6;

            // Idle Animation
            const waveX = Math.sin(originalX * 0.1 + time * 0.5);
            const waveY = Math.cos(originalY * 0.1 + time * 0.5);
            const idleZ = (waveX + waveY) * 0.5;

            // Rotation Physics
            // Cubes lean towards mouse + slight idle wobble + center attraction
            const tiltX = -(originalY / 40) * leanStrength;
            const tiltY = (originalX / 40) * leanStrength;

            const rotX = proximity * (dy / safeDist) * 0.5 + tiltX;
            const rotY = proximity * (dx / safeDist) * 0.5 + tiltY;

            const targetZ = idleZ + bulge;

            dummy.position.set(originalX, originalY, targetZ);
            dummy.scale.set(maskStrength, maskStrength, maskStrength);
            dummy.rotation.set(rotX, -rotY, 0);

            dummy.updateMatrix();
            meshRef.current!.setMatrixAt(i, dummy.matrix);
        });

        meshRef.current.instanceMatrix.needsUpdate = true;
    });

    // Color Palette for "Tech" look
    const colorPalette = [
        "#ffffff", // White (Default)
        "#e0f2fe", // Sky 100
        "#bae6fd", // Sky 200
        "#7dd3fc", // Sky 300
        "#60a5fa", // Blue 400
        "#818cf8", // Indigo 400
    ];

    // Initialize Random Colors
    useEffect(() => {
        if (!meshRef.current) return;

        const tempColor = new THREE.Color();

        for (let i = 0; i < count; i++) {
            // 85% chance of being standard white (to keep image visible)
            // 15% chance of being a "glitch" or "tech" blue pixel
            if (Math.random() > 0.85) {
                const colorHex = colorPalette[Math.floor(Math.random() * (colorPalette.length - 1)) + 1] || "#ffffff";
                tempColor.set(colorHex);
            } else {
                tempColor.set("#ffffff");
            }
            meshRef.current.setColorAt(i, tempColor);
        }
        meshRef.current.instanceColor!.needsUpdate = true;
    }, [count]);


    // Custom Shader Logic: Mosaic Projection + Elevation Glow
    const materialOnBeforeCompile = (shader: any) => {
        shader.uniforms.uGridWidth = { value: gridWidth };
        shader.uniforms.uGridHeight = { value: gridHeight };

        shader.vertexShader = `
      uniform float uGridWidth;
      uniform float uGridHeight;
      attribute vec3 aPos;
      varying float vElevation;
    ` + shader.vertexShader;

        shader.vertexShader = shader.vertexShader.replace(
            '#include <uv_vertex>',
            `
      #ifdef USE_UV
        // Mosaic Mapping
        vec2 combinedPos = aPos.xy + position.xy;
        vMapUv = vec2(
            (combinedPos.x / uGridWidth) + 0.5,
            (combinedPos.y / uGridHeight) + 0.5
        );
      #endif
      
      // Calculate Elevation for Glow
      // instanceMatrix[3].z is the z-translation
      vElevation = instanceMatrix[3].z;
      `
        );

        shader.fragmentShader = `
      varying float vElevation;
      ` + shader.fragmentShader;

        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <dithering_fragment>',
            `
      #include <dithering_fragment>
      
      // Elevation Glow
      // When cubes rise (vElevation > 0), make them brighter/whiter
      // Range of bulge is roughly 0 to 6
      
      float glowStrength = smoothstep(0.0, 5.0, vElevation);
      vec3 glowColor = vec3(0.8, 0.9, 1.0); // Light Blue-White
      
      // Add glow to final color
      gl_FragColor.rgb += glowColor * glowStrength * 0.6;
      `
        );
    };

    // Update material to use the texture
    useEffect(() => {
        if (materialRef.current) {
            materialRef.current.needsUpdate = true;
        }
    }, [texture]);

    return (
        <group>
            <ambientLight intensity={0.5} />
            <directionalLight position={[10, 10, 30]} intensity={1} color="#ffffff" />
            <directionalLight position={[-10, -10, 30]} intensity={0.5} color="#a5b4fc" />

            <instancedMesh ref={meshRef} args={[undefined, undefined, count]}>
                <boxGeometry args={[1.45, 1.45, 1.45]}>
                    <instancedBufferAttribute attach="attributes-aPos" args={[posArray, 3]} />
                </boxGeometry>
                <meshStandardMaterial
                    ref={materialRef}
                    color="#1a1a2e"
                    transparent={true}
                    opacity={0.5}
                    roughness={0.3}
                    metalness={0.8}
                    onBeforeCompile={materialOnBeforeCompile}
                />
            </instancedMesh>
        </group>
    );
};
